import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { RefreshTokenResponse } from '../models/auth.model';

let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  
  // Skip auth header for auth endpoints
  if (isAuthEndpoint(request.url)) {
    return next(request);
  }

  // Add auth header if token exists
  const token = authService.getToken();
  if (token) {
    request = addTokenHeader(request, token);
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isAuthEndpoint(request.url) && !isNoRetryEndpoint(request.url)) {
        return handle401Error(request, next, authService);
      }
      return throwError(() => error);
    }),
  );
};

function isAuthEndpoint(url: string): boolean {

  // Public auth endpoints that don't need token
  const publicAuthEndpoints = [
    // '/',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/refresh',
    '/public/rotations', // Rotas públicas de rotações
    '/public/patrol-routes', // Rotas públicas de patrulhas
    `/public/positions`
  ];

  const isPublic = publicAuthEndpoints.some((endpoint) => {
    const matches = url.includes(endpoint);
    // console.log(`  ${endpoint}: ${matches}`); 
    return matches;
  });
  
  // console.log('🎯 Resultado:', publicAuthEndpoints.some((endpoint) => url.includes(endpoint)));
  // console.log(isPublic)
  return isPublic;

  // return publicAuthEndpoints.some((endpoint) => url.includes(endpoint));
}

function isNoRetryEndpoint(url: string): boolean {
  // Endpoints where 401 means "operation not allowed" not "token expired"
  // These should not trigger automatic token refresh or logout
  const noRetryEndpoints = [
    '/auth/admin/reset-supervisor-password',
    '/auth/admin/',
    '/users/reset-password',
    '/supervisor-schedules',
    '/supervisor-check-ins',
  ];

  return noRetryEndpoints.some((endpoint) => url.includes(endpoint));
}

function addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${token}`),
  });
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = authService.getRefreshToken();
    if (refreshToken) {
      return authService.refreshToken().pipe(
        switchMap((response: RefreshTokenResponse) => {
          isRefreshing = false;
          refreshTokenSubject.next(response.token);
          return next(addTokenHeader(request, response.token));
        }),
        catchError((error) => {
          isRefreshing = false;
          authService.logout();
          return throwError(() => error);
        }),
      );
    } else {
      isRefreshing = false;
      if (authService.isCurrentTokenExpired()) {
        authService.logout();
        return EMPTY;
      }
      return throwError(() => new Error('No refresh token available'));
    }
  }

  return refreshTokenSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap((token) => next(addTokenHeader(request, token))),
  );
}
